import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import s from './audio.module.scss';
import ReactAudioPlayer from 'react-audio-player';
import { useRef, useState } from 'react';

export const TabAudio = () => {
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>();
  const durationRef = useRef<number>();
  const audioPlayerRef = useRef<ReactAudioPlayer>();

  return (
    <div className={s.wrapper}>
      <div className={s.transcribe}>Please transcribe this audio.</div>
      <div className={s['content-box']}>
        <div className="flex items-center space-x-2 lg:space-x-3 mb-2 lg:mb-3 xl:mb-5">
          <MusicIcon />
          <div>
            <p className="text-[10px] lg:text-xs">steve-jobs-speech.mp3</p>
            <p className="text-[8px] lg:text-[10px] text-stone-400">0:00</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              if (!audioPlayed) {
                setAudioPlayed(true);
                return;
              }
              if (!audioPlayerRef.current) return;

              if (playing) audioPlayerRef.current.audioEl.current.pause();
              else audioPlayerRef.current.audioEl.current.play();
            }}
          >
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>

          <div className={s['progress-bar']}>
            <div ref={progressBarRef} className={s.progress} />
          </div>
        </div>
      </div>
      <div className={s['content-box--code']}>
        <SyntaxHighlighter
          style={atomDark}
          language="javascript"
          showLineNumbers
          customStyle={{
            fontFamily: 'DM Mono',
            height: '100%',
            padding: 0,
            margin: 0,
            background: 'transparent'
          }}
        >
          {`client.createAudioSpeechTask({ 
  "attachment_type": "audio", 
  "attachment": "THE_URL_WE_UPLOAD_THIS_TO", 
  "verbatim": true, 
  "callback_url": "http://www.example.com/callback", 
  }, (err, task) => { 
  // do something with task 
});`}
        </SyntaxHighlighter>

        {!audioPlayed && (
          <button
            className={s.button}
            onClick={() => {
              setAudioPlayed(true);
              setPlaying(true);
            }}
          >
            Run Code
          </button>
        )}

        {audioPlayed && (
          <ReactAudioPlayer
            autoPlay
            listenInterval={500}
            src="/static/audio/transcription.mp3"
            onLoadedMetadata={e => (durationRef.current = (e.target as HTMLAudioElement).duration)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => (progressBarRef.current.style.width = '0')}
            ref={element => {
              audioPlayerRef.current = element;
            }}
            onListen={currentTime =>
              (progressBarRef.current.style.width = `${
                100 - (currentTime / durationRef.current) * 100
              }%`)
            }
          />
        )}
      </div>

      {audioPlayed && (
        <div className={s.result}>
          <SyntaxHighlighter
            style={atomDark}
            language="json"
            customStyle={{
              fontFamily: 'DM Mono',
              height: '100%',
              padding: 10,
              margin: 0,
              fontSize: 8,
              background: 'transparent'
            }}
          >
            {`{
  "transcript": "On the back cover of their final issue was a photograph of an early morning country road, the kind you might find yourself hitchhiking on if you were so adventurous. Beneath it were the words: “Stay Hungry. Stay Foolish.” It was their farewell message as they signed off. Stay Hungry. Stay Foolish. And I have always wished that for myself. And now, as you graduate to begin anew, I wish that for you.Stay Hungry. Stay Foolish. Thank you all very much.",
  "duration": 0.36
}`}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
};

const MusicIcon = () => {
  return (
    <svg width="32" height="32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle
        cx="16"
        cy="16"
        r="15.556"
        fill="#585858"
        stroke="url(#paint0_linear_8067_20264)"
        strokeWidth=".889"
      />
      <path
        d="M13.361 19.5v-7.583l7-1.167v7.583"
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.61 21.25a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5ZM18.611 20.084a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z"
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id="paint0_linear_8067_20264"
          x1="82.5"
          y1="-28.5"
          x2="-33.825"
          y2="7.807"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const PlayIcon = () => {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="m1.515.91 4.242 2.726-4.242 2.727V.91Z"
        fill="#CFCFCF"
        stroke="#CFCFCF"
        strokeWidth=".606"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const PauseIcon = () => {
  return (
    <svg width="8" height="8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.758 2.076H2.546v4.848h1.212V2.076ZM6.18 2.076H4.968v4.848H6.18V2.076Z"
        fill="#CFCFCF"
        stroke="#CFCFCF"
        strokeWidth=".606"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
